import { termMaxApiBase } from '../../lending/public-data/termmax/apiClient'
import { GenericTokenList } from '../../types/general'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import type { TermMaxVault, TermMaxVaults } from './types'

/**
 * TermMax vault fetch via `GET /vault/list?chainId=` — the primary source.
 *
 * Strictly better than the on-chain path for three reasons, each of which bit
 * during development:
 *
 *  1. **`apr()` reverts** on the deployed 2.0.0 vaults (so does
 *     `accretingPrincipal()`), so on-chain the rate has to be derived. The API
 *     serves it directly, already net of the performance fee.
 *  2. **Curator NAMES are only here.** The on-chain surface exposes just an
 *     address, and guessing the name from the vault name is actively wrong:
 *     `"Coinshift rlUSD vault"` is curated by **Keyrock**.
 *  3. Caps, idle funds, rewards and the base-yield pool are not cheaply
 *     readable on-chain.
 *
 * The on-chain reader in `fetchFromChain.ts` stays as the fallback, and its
 * derivation was cross-validated against this endpoint: for both funded
 * Ethereum vaults the derived net APR matched `apr` to 3 decimals.
 */

const LENDING_DEBUG = false
const warn = (...args: unknown[]) => {
  if (LENDING_DEBUG) console.warn('[termmax-vaults]', ...args)
}

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const big = (v: unknown): string => {
  try {
    if (v === undefined || v === null || v === '') return '0'
    const s = String(v)
    return BigInt(s.includes('.') ? s.slice(0, s.indexOf('.')) : s).toString()
  } catch {
    return '0'
  }
}
const lower = (v: unknown): string => String(v ?? '').toLowerCase()

/**
 * Fetch a chain's TermMax vaults from the API, keyed by lowercased vault
 * address. Returns null (not `{}`) on failure so the caller can distinguish
 * "upstream down → try on-chain" from "this chain genuinely has no vaults".
 */
export async function fetchTermMaxVaultsFromApi(
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  fetchImpl: typeof fetch = fetch,
): Promise<TermMaxVaults | null> {
  const url = `${termMaxApiBase(chainId)}/vault/list?chainId=${chainId}`
  let rows: any[]
  try {
    const res = await fetchImpl.call(globalThis, url)
    if (!res.ok) {
      warn(`vault/list ${chainId} → HTTP ${res.status}`)
      return null
    }
    const json: any = await res.json()
    rows = Array.isArray(json?.data) ? json.data : []
  } catch (e) {
    warn(`vault/list ${chainId} failed:`, (e as any)?.message ?? e)
    return null
  }

  const out: TermMaxVaults = {}
  for (const r of rows) {
    const address = lower(r?.vaultAddress)
    const underlying = lower(r?.asset?.contractAddress)
    if (!address || !underlying) continue
    // `isEnabled: false` rows are retired; keep paused ones (a paused vault
    // still holds depositor funds that must stay visible).
    if (r.isEnabled === false) continue

    const assetDecimals = num(r?.asset?.decimals) || 18
    const scale = 10 ** assetDecimals

    const assetEntry = tokenList[underlying]
    const priceKey =
      toOracleKey(assetEntry?.assetGroup!) ||
      toGenericPriceKey(underlying, chainId)
    const priceUsd = prices[priceKey]

    const totalAssets = big(r.totalAssets)
    const totalSupply = big(r.totalSupplyAmt)
    const totalAssetsFormatted = Number(totalAssets) / scale

    // `apr` is a FRACTION (0.02578944 = 2.579%), already net of the
    // performance fee — the same quantity the on-chain path derives as
    // `annualizedInterest · (1 − feeRate) / accretingPrincipal`.
    const supplyRate = num(r.apr) * 100
    // TMX emissions + any third-party reward APR, when the vault has them.
    const rewardsRate = num(r?.incentiveData?.TMX_APR) * 100

    // Withdrawable NOW. A TermMax vault's capital sits in maker orders until
    // maturity, so only `redeemableAmt` (falling back to idle) is instantly
    // exitable — deliberately NOT `totalAssets`.
    //
    // CLAMPED to `totalAssets`: upstream reports the two from slightly
    // different snapshots, and `redeemableAmt` can come back a few wei ABOVE
    // `totalAssets` (observed on BNB). Publishing "more withdrawable than the
    // vault holds" would break the downstream liquidity ≤ totalAssets
    // invariant, so the excess is trimmed rather than passed through.
    const rawLiquidity = BigInt(big(r.redeemableAmt ?? r.idleFunds ?? '0'))
    const totalAssetsBig = BigInt(totalAssets)
    const liquidity = (
      rawLiquidity > totalAssetsBig ? totalAssetsBig : rawLiquidity
    ).toString()
    const liquidityFormatted = Number(liquidity) / scale

    const totalSupplyValue = num(r.totalSupplyValue)

    out[address] = {
      address,
      underlying,
      symbol: String(r.symbol ?? ''),
      name: String(r.name ?? ''),
      // Share decimals are not reported; TermMax vault shares track the
      // asset's decimals (verified on-chain: 6-dec USDC vault, 18-dec WETH).
      decimals: assetDecimals,
      assetDecimals,
      totalAssets,
      totalSupply,
      convertToAssets:
        BigInt(totalSupply) > 0n
          ? (
              (BigInt(totalAssets) * 10n ** BigInt(assetDecimals)) /
              BigInt(totalSupply)
            ).toString()
          : '0',
      supplyRate,
      rewardsRate,
      depositRate: supplyRate + rewardsRate,
      // 1e8-scaled upstream (10000000 = 10%).
      fee: num(r.performanceFeeRate) / 1e6,
      timelock: num(r.timeLockSeconds) || undefined,
      curator: r?.curator?.curatorAddress
        ? lower(r.curator.curatorAddress)
        : undefined,
      // The API is the ONLY source for this — do not derive it from `name`.
      curatorName: r?.curator?.name || undefined,
      guardian: r?.guardianAddress ? lower(r.guardianAddress) : undefined,
      asset: assetEntry,
      priceUsd,
      totalAssetsFormatted,
      totalAssetsUsd:
        totalSupplyValue > 0
          ? totalSupplyValue
          : priceUsd
            ? totalAssetsFormatted * priceUsd
            : 0,
      liquidity,
      liquidityFormatted,
      liquidityUsd: priceUsd ? liquidityFormatted * priceUsd : 0,
      version: r.version,
      isPaused: r.isPaused === true || undefined,
      supplyCap: r.capacity ? big(r.capacity) : undefined,
      basePool: r.poolAddress ? lower(r.poolAddress) : undefined,
    } as TermMaxVault
  }

  return out
}
